import _ from 'lodash';
import moment from 'moment';
import React, { createContext, useContext, useEffect, useState } from 'react';
import ReactGA from 'react-ga';
import { getWithTokenRefresh } from 'services/clients/axios-with-token';
import { SegmentService } from 'services/helpers/segment';
import { Profile, User } from 'types/User';
import { objectKeysToCamelCase } from 'utils/common';

interface UserContextValues {
  user: User | null;
  isLoading: boolean;
  fetchUser: () => Promise<User | undefined>;
}

const UserContext = createContext<UserContextValues | undefined>(undefined);

const getContainerCreditsLimitReached = (profile: Profile) => {
  const containerCreditsLimit = _.get(profile, 'company.container_credits_limit');
  const containerCreditsUsed = _.get(profile, 'company.container_credits_used');
  if (
    containerCreditsLimit === undefined ||
    containerCreditsLimit === null ||
    containerCreditsUsed === undefined ||
    containerCreditsUsed === null
  ) {
    return false;
  }
  return containerCreditsUsed >= containerCreditsLimit;
};

const getFreeTrialRemainingDays = (profile: Profile) => {
  const trialEnd = profile.company?.trial_end;
  const companyPack = _.get(profile, 'company.pack');
  if (companyPack !== 'pioneer') {
    return null;
  }
  const days = Math.floor(moment.duration(moment(trialEnd).diff(moment())).asDays());
  if (days < 0) {
    return null;
  }
  return days;
};

const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUser = React.useCallback(async () => {
    const profile: Profile = await getWithTokenRefresh('/core/api/v1/auth/profile');

    let teamMembers = [];
    try {
      teamMembers = await getWithTokenRefresh('/core/api/v1/auth/company/members');
    } catch {
      // this may fail with a 403, in which cse do nothing
    }
    const u: User = {
      ...profile,
      teamMembers,
      containerCreditsLimitReached: getContainerCreditsLimitReached(profile),
      freeTrialRemainingDays: getFreeTrialRemainingDays(profile),
      company: profile.company && {
        ...profile.company,
        port_collections: objectKeysToCamelCase(profile.company?.port_collections),
      },
    };
    setUser(u);
    return u;
  }, [setUser]);

  useEffect(() => {
    (async () => {
      await fetchUser();
      setIsLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (user) {
      SegmentService.identifyUser(user);
      ReactGA.set({ userId: user.id });
    }
  }, [user]);

  // NOTE: you *might* need to memoize this value
  // Learn more in http://kcd.im/optimize-context
  const value = React.useMemo(() => ({ user, isLoading, fetchUser }), [user, isLoading, fetchUser]);
  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
UserContext.displayName = 'UserContext';

function useUserContext() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}

export { UserProvider, useUserContext, UserContext };
